import axios from 'axios';
import { configService } from '../services/configService';
import { getCachedAccessToken, getCachedRefreshToken, getAccessToken } from '../services/identityService';
import { getTokensFromSSO } from '../utilities/identity';

export const secureService = axios.create();

/**
 * Intercepts requests to add a access token to the request headers if a token is cached or if a token can be retrieved from SSO
 */
secureService.interceptors.request.use(async (config) => {
    const cachedAccessToken = getCachedAccessToken();
    const headers = { ...config?.headers };

    if (cachedAccessToken) {
        headers.Authorization = `Bearer ${cachedAccessToken.accessToken}`
        return {
            ...config,
            headers
        }
    }

    const cachedRefreshToken = getCachedRefreshToken();
    if (cachedRefreshToken) {
        const accessToken = await getAccessToken(cachedRefreshToken.refreshToken, configService.appId);

        headers.Authorization = `Bearer ${accessToken.accessToken}`
        return {
            ...config,
            headers
        }
    }

    const cwTokens = await getTokensFromSSO();
    if (cwTokens?.accessToken) {
        headers.Authorization = `Bearer ${cwTokens.accessToken.accessToken}`
        return {
            ...config,
            headers
        }
    }

    const controller = new AbortController();
    controller.abort();

    return {
        ...config,
        signal: controller.signal
    }
}, (err) => {
    console.log('request error interceptor', err);
    return Promise.reject(err);
});

const IDEMPOTENT_METHODS = ['get', 'head', 'options'];

secureService.interceptors.response.use((response) => {
    return response;
}, async (err) => {
    const originalRequest = err.config;

    // 503 retry with backoff (services waking up). Idempotent methods only —
    // replaying a POST/PUT/DELETE on 503 risks duplicate creates or double-applied
    // mutations when the server actually processed the request before the 503.
    if (err.response?.status === 503 && originalRequest) {
        const method = (originalRequest.method || '').toLowerCase();
        if (!IDEMPOTENT_METHODS.includes(method)) {
            console.warn(
                `secureService - 503 on non-idempotent ${method.toUpperCase()}, not retrying`,
                originalRequest.url
            );
            return Promise.reject(err);
        }

        const retryCount = originalRequest._galaxy503RetryCount || 0;
        const retryDelays = [1000, 3000, 5000]; // 1s, 3s, 5s

        if (retryCount < retryDelays.length) {
            originalRequest._galaxy503RetryCount = retryCount + 1;
            console.warn(
                `secureService - 503 received, retry ${retryCount + 1}/${retryDelays.length} in ${retryDelays[retryCount]}ms`,
                originalRequest.url
            );
            await new Promise(resolve => setTimeout(resolve, retryDelays[retryCount]));
            return secureService(originalRequest);
        }

        console.error('secureService - 503 retries exhausted', originalRequest.url);
    }

    console.log('response error interceptor', err);
    return Promise.reject(err);
});